import { Avatar, Box, Button, Grid, IconButton, InputAdornment, TextField } from '@mui/material';
import { ModeEditSharp, Visibility, VisibilityOff } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import React, { ChangeEvent, useContext, useEffect, useRef, useState } from 'react';
import { stringToColor } from '../../utils/stringToColor.ts';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ALPHABET_REGEX, IRAN_MOBILE_REGEX, NATIONAL_ID_REGEX } from '../../constants/regex.ts';
import { UserContext } from '../../contexts/UserContext.tsx';
import axios from 'axios';
import IResponse from '../../interfaces/response.ts';
import { LoadingButton } from '@mui/lab';
import { DatePicker, PickersLocaleText } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFnsJalali } from '@mui/x-date-pickers/AdapterDateFnsJalaliV3';

const Account = () => {
	const { t } = useTranslation();

	const { user } = useContext(UserContext);

	const [showPassword, setShowPassword] = React.useState(false);
	const [selectedImage, setSelectedImage] = useState<any>(user?.image);
	const [loading, setLoading] = useState(false);
	const [birthDate, setBirthDate] = useState<Date | null>(null);

	const inputImageRef = useRef<HTMLInputElement | null>(null);

	const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
		const file = event?.target?.files?.[0];
		if (!file) {
			setSelectedImage('');
			return;
		}

		if (file && file.type.startsWith('image/')) {
			const data = new FormData();
			data.append('image', file);
			axios<IResponse<string>>({
				url: '/user/upload-profile',
				method: 'POST',
				data,
			});
			const reader = new FileReader();
			reader.onloadend = () => {
				setSelectedImage(reader.result as string);
			};
			reader.readAsDataURL(file);
		} else {
			setSelectedImage('');
		}
	};

	const handleButtonClick = () => {
		inputImageRef?.current?.click();
	};

	const userAccountSchema = z.object({
		email: z.string().email(t('invalid_email_message')),
		password: z.union([z.string().trim().min(8, t('invalid_password_message_8_karakter')), z.string().length(0)]),
		phone: z.string().regex(IRAN_MOBILE_REGEX, {
			message: t('invalid_mobile_message'),
		}),
		first_name: z
			.string()
			.min(3, t('invalid_min_name_message'))
			.regex(ALPHABET_REGEX, {
				message: t('invalid_name_message'),
			}),
		last_name: z
			.string()
			.min(3, t('invalid_min_name_message'))
			.regex(ALPHABET_REGEX, {
				message: t('invalid_name_message'),
			}),
		company_name: z.string().min(1, t('invalid_company_message')).optional(),
		nation_code: z.string().regex(NATIONAL_ID_REGEX, {
			message: t('invalid_national_identification_number_message'),
		}),
	});

	type FormType = z.infer<typeof userAccountSchema>;

	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
	} = useForm<FormType>({
		resolver: zodResolver(userAccountSchema),
	});

	const handleClickShowPassword = () => setShowPassword(show => !show);

	const handleSaveInfo = (data: FormType) => {
		setLoading(true);
		const userDate = birthDate ? new Date(birthDate) : null;
		axios({
			method: 'PUT',
			url: 'user/update',
			data: {
				...data,
				birth_date: userDate ? userDate.toISOString().split('T')[0] : undefined,
			},
		}).finally(() => {
			setLoading(false);
		});
	};

	const handleChangeBirthDate = (value: Date | null) => {
		setBirthDate(value);
	};

	const customPtBRLocaleText: Partial<PickersLocaleText<any>> = {
		okButtonLabel: t('confirm'),
		cancelButtonLabel: t('cancel'),
	};

	useEffect(() => {
		if (user?.birth_date) {
			setBirthDate(new Date(user.birth_date));
		}
	}, [user]);

	const handleCancel = () => {
		reset();
	};

	return (
		<div className="relative w-full rounded-2xl bg-white">
			<div className="w-full">
				<div className="relative min-h-24 rounded-se-2xl rounded-ss-2xl bg-dashboard-header-background bg-cover bg-no-repeat"></div>

				<Avatar src={selectedImage} className="absolute top-11 mr-10 h-24 w-24 border-4 border-white" sx={{ bgcolor: stringToColor(user?.name ?? '') }}>
					{(user?.name ?? '')
						?.split(' ')
						.slice(0, 2)
						?.map(i => i?.[0])}
				</Avatar>

				<Box className="absolute right-8 top-[103px] rounded-full border-4 border-white">
					<IconButton aria-label="edit" className="bg-primary-main" onClick={handleButtonClick}>
						<ModeEditSharp className="text-sm text-white" />
					</IconButton>
					<input accept="image/*" type="file" hidden ref={inputImageRef} onChange={handleImageChange} />
				</Box>
			</div>
			<form className="mt-20 w-full rounded p-6 text-base" onSubmit={handleSubmit(handleSaveInfo)} autoComplete="off">
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6}>
						<TextField fullWidth variant="outlined" label={t('first_name')} {...register('first_name')} error={!!errors.first_name} helperText={errors.first_name?.message} inputProps={{ maxLength: 50 }} defaultValue={user?.first_name} />
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField fullWidth variant="outlined" label={t('first_name_and_last_name')} {...register('last_name')} error={!!errors.last_name} helperText={errors.last_name?.message} inputProps={{ maxLength: 50 }} defaultValue={user?.last_name} />
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField fullWidth variant="outlined" label={t('phone_number')} {...register('phone')} error={!!errors.phone} helperText={errors.phone?.message} inputProps={{ maxLength: 11 }} placeholder="09123456789" defaultValue={user?.phone} />
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField fullWidth variant="outlined" label={t('email')} type="email" {...register('email')} error={!!errors.email} helperText={errors.email?.message} defaultValue={user?.email} />
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField fullWidth variant="outlined" disabled={true} label={t('nation-code')} {...register('nation_code')} error={!!errors.nation_code} helperText={errors.nation_code?.message} defaultValue={user?.nation_code} inputProps={{ maxLength: 10 }} />
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField fullWidth variant="outlined" disabled={true} label={t('company_name')} {...register('company_name')} error={!!errors.company_name} helperText={errors.company_name?.message} defaultValue={user?.company_name} />
					</Grid>
					<Grid item xs={12} sm={6}>
						{/* <InputLabel className="float-start flex px-2 pb-2">{t('date_of_birth')}</InputLabel> */}
						<LocalizationProvider dateAdapter={AdapterDateFnsJalali} localeText={customPtBRLocaleText}>
							<DatePicker className="w-full" label={t('date_of_birth')} value={birthDate} onChange={handleChangeBirthDate} />
						</LocalizationProvider>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField
							fullWidth
							variant="outlined"
							label={t('password')}
							type={showPassword ? 'password' : 'text'}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										<IconButton aria-label="toggle password visibility" onClick={handleClickShowPassword} edge="end">
											{showPassword ? <VisibilityOff /> : <Visibility />}
										</IconButton>
									</InputAdornment>
								),
							}}
							autoComplete="new-password"
							{...register('password')}
							error={!!errors.password}
							helperText={errors.password?.message}
						/>
					</Grid>
					<Grid item xs={12} className="mr-auto flex flex-row-reverse md:max-w-[50%]">
						<div className="flex w-full flex-row gap-3">
							<Button variant="outlined" color="error" className="mb-2 w-28" onClick={handleCancel}>
								{t('deny')}
							</Button>
							<LoadingButton loading={loading} variant="contained" className="mb-2 grow" type="submit">
								{t('edit_information')}
							</LoadingButton>
						</div>
					</Grid>
				</Grid>
			</form>
		</div>
	);
};

export default Account;
